// Energized Electrical Work Permit, Stage 5 piece 5. Words: EEWP_WORDS.md rev 2 (approved by Robert
// 2026-10-12). Mockup: MOCKUP_EEWP.html rev 2b (approved 2026-10-12).
//
// PR 1: approvers by site. Every contact ticked EEWP Approver at a site must say yes (rev 3, Robert
// 2026-10-12: the ticks decide, no separate number). Up to 3 per site. Approvers are supervisors or
// managers, never a QEW: a ticked contact whose email belongs to an Active QEW does not count, and the
// server refuses the tick. A site with no approvers is Short. EEWP Awareness ticks get an email, no limit.
//
// Pure: data in, data out. Shared by the page, the server steps and the unit tests.

import { energizedByNature, isDead, verdictInputOf, type JspAsset, type JspState, type JspTask } from "./jsp";
import { verdict } from "./risk-verdict";
import type { Contact, Site } from "./sites";

/** Every ticked approver must say yes. More than 3 at 2 AM is too many. */
export const MAX_APPROVERS = 3;

export const NOT_A_QEW = "Approvers are supervisors or managers. This email belongs to an Active QEW, so it cannot be an EEWP Approver.";
export const NO_APPROVERS = "No EEWP approvers set up. See your ESP Manager.";
export const TOO_MANY_APPROVERS = `A site has up to ${MAX_APPROVERS} EEWP Approvers. Every one must say yes. Untick one first.`;

/** Emails of Active QEWs, lower case. People with no email are skipped. */
export function qewEmailsOf(people: { email?: string | null; isActive?: boolean }[]): Set<string> {
  return new Set(people.filter((p) => p.isActive !== false && p.email).map((p) => String(p.email).trim().toLowerCase()));
}

export function isQewEmail(email: string, qewEmails: ReadonlySet<string>): boolean {
  return qewEmails.has(email.trim().toLowerCase());
}

export interface EewpSiteRow {
  siteNumber: string;
  name: string;
  /** Active, ticked, not a QEW. Sorted by name. Every one must say yes. */
  approvers: string[];
  /** Ticked but the email belongs to an Active QEW. Not counted. */
  qewTicked: string[];
  /** Active, ticked EEWP Awareness. */
  awareness: string[];
  /** No approvers: QEWs at this site see NO_APPROVERS. */
  short: boolean;
}

/** The ticked approvers at one site, split into those who count and those who are QEWs. */
export function approversAt(contacts: Contact[], siteNumber: string, qewEmails: ReadonlySet<string>): { approvers: Contact[]; qewTicked: Contact[] } {
  const ticked = contacts
    .filter((c) => c.scope === "site" && c.siteNumber === siteNumber && c.isActive && c.usedFor?.eewpApprover)
    .sort((a, b) => a.name.localeCompare(b.name));
  return {
    approvers: ticked.filter((c) => !isQewEmail(c.email, qewEmails)),
    qewTicked: ticked.filter((c) => isQewEmail(c.email, qewEmails)),
  };
}

/** Active contacts ticked EEWP Awareness at one site. Sorted by name. No limit. */
export function awarenessAt(contacts: Contact[], siteNumber: string): Contact[] {
  return contacts
    .filter((c) => c.scope === "site" && c.siteNumber === siteNumber && c.isActive && c.usedFor?.eewpAwareness)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** One row per site, in site number order. Inactive sites are left off. */
export function eewpSiteRows(sites: Site[], contacts: Contact[], qewEmails: ReadonlySet<string>): EewpSiteRow[] {
  return sites
    .filter((s) => s.isActive !== false)
    .sort((a, b) => a.siteNumber.localeCompare(b.siteNumber))
    .map((s) => {
      const { approvers, qewTicked } = approversAt(contacts, s.siteNumber, qewEmails);
      return {
        siteNumber: s.siteNumber,
        name: s.name || `Site ${s.siteNumber}`,
        approvers: approvers.map((c) => c.name),
        qewTicked: qewTicked.map((c) => c.name),
        awareness: awarenessAt(contacts, s.siteNumber).map((c) => c.name),
        short: approvers.length === 0,
      };
    });
}

/**
 * The cap. Null when the save is fine. Ticking a 4th active EEWP Approver at a site is refused.
 * QEW ticks never count, so they never fill a seat.
 */
export function approverCapProblem(contacts: Contact[], siteNumber: string, contactId: string, ticking: boolean, qewEmails: ReadonlySet<string>): string | null {
  if (!ticking) return null;
  const others = approversAt(contacts, siteNumber, qewEmails).approvers.filter((c) => c.contactId !== contactId);
  return others.length >= MAX_APPROVERS ? TOO_MANY_APPROVERS : null;
}

/** "2 must approve" */
export function mustApprove(n: number): string {
  return n === 1 ? "1 must approve" : n === 2 ? "Both must approve" : `All ${n} must approve`;
}

// ---- PR 2: the QEW side (EEWP_WORDS.md rev 2, mockup rev 2b) ----
// The link shows only where the QEW's own answers already point to live work. A small grey link,
// never a button. Two Before You Ask screens push back first. Then the seven questions, the JSP's
// half frozen, and Submit For Approval. The JSP waits. No work starts. Approvals come in PR 3.


/** What the JSP page needs to know about the EEWP at this asset. Null on a demo asset or with EEWP off. */
export interface EewpContext {
  /** Program Settings, EEWP On. */
  on: boolean;
  /** Program Settings, Require An EEWP For Diagnostic Cover Removal. */
  coverRequired: boolean;
  /** Ticked approvers at the asset's site. Every one must say yes. Frozen on each permit when it is asked. */
  required: number;
  /** No approvers: the link becomes NO_APPROVERS. */
  short: boolean;
}

/** Where the QEW tapped the link. */
export type EewpFrom = "needPower" | "coverRemoval" | "lockOut" | "over40";
export type EewpScope = "full" | "coverRemoval";

export const EEWP_EXPIRY_HOURS = 12;

/** EEWP-1004 from JSP-1004. One JSP, one permit. */
export function permitIdOf(jspNumber: string): string {
  return `EEWP-${jspNumber.replace(/^JSP-/, "")}`;
}

export function expiresAtOf(createdIso: string): string {
  return new Date(new Date(createdIso).getTime() + EEWP_EXPIRY_HOURS * 3600 * 1000).toISOString();
}

export function scopeOf(from: EewpFrom): EewpScope {
  return from === "coverRemoval" ? "coverRemoval" : "full";
}

/** IE above 40 under the ceiling: an approved EEWP would lift the DANGER (decision 41). Never while the ceiling is 12. */
export function over40Helps(a: JspAsset, t: JspTask, s: JspState): boolean {
  const input = verdictInputOf(a, t, s);
  return verdict(input).forcedDeEnergize && !verdict({ ...input, approvedEewp: true }).forcedDeEnergize;
}

/** The link this node shows, or null. Tier 1 never. Nothing while EEWP On is off. */
export function eewpLinkAt(node: string, a: JspAsset, t: JspTask, s: JspState, ctx: EewpContext | null | undefined): EewpFrom | null {
  if (!ctx?.on || t.tier === 1) return null;
  if (node === "Do The Work" && t.tier === 2 && !energizedByNature(t) && s.plan === "live" && !isDead(t, s)) return "needPower";
  if (node === "PPE And Boundary" && t.ppeMode === "ir" && s.irCover === "yes" && !isDead(t, s)) return "coverRemoval";
  if (node === "Lock Out" && t.tier === 3) return "lockOut";
  if (node === "Risk" && !s.deen && over40Helps(a, t, s)) return "over40";
  return null;
}

/** The quiet line: the lead-in, then the link words. */
export function linkWords(from: EewpFrom, coverRequired: boolean): [string, string] {
  switch (from) {
    case "needPower":
      return ["Diagnosis turning into a repair?", "Request An EEWP"];
    case "coverRemoval":
      return [coverRequired ? "Required at this site." : "Optional at this site.", "Request EEWP For Cover Removal"];
    case "lockOut":
      return ["Can’t establish an electrically safe work condition?", "Request An EEWP"];
    case "over40":
      return ["Must it stay energized?", "Request An EEWP"];
  }
}

/** Diagnostic Cover Removal on: the IR scan with a bolted panel off waits for an approved EEWP. */
export function coverNeedsPermit(t: JspTask, s: JspState, ctx: EewpContext | null | undefined, permit: EewpSummary | null): boolean {
  return Boolean(ctx?.on && ctx.coverRequired) && t.ppeMode === "ir" && s.irCover === "yes" && permit?.status !== "Approved";
}

// ---- Before You Ask ----

export const NFPA_REASONS: [string, string][] = [
  ["De-energizing creates a greater hazard.", "Life support, alarms, ventilation, an unsafe process stop."],
  ["The task is infeasible de-energized.", "Equipment design or operating limits."],
];
export const NOT_REASONS = "Convenience, production schedule and cost are not reasons.";

// ---- The QEW's half ----

export const JUSTIFY_OPTIONS = [
  ["greaterHazard", "Greater Hazard"],
  ["infeasible", "Infeasible By Design Or Operation"],
] as const;
export type JustifyKey = (typeof JUSTIFY_OPTIONS)[number][0];

export interface EewpAnswers {
  /** 1. What exactly has to be done live? */
  liveWork: string;
  /** 2. Why it cannot be de-energized (130.2(A)), then the specifics. */
  justificationCategories: JustifyKey[];
  justification: string;
  /** 3. What happens if it is de-energized? */
  ifDeEnergized: string;
  /** 4. Can part of the job be done dead? */
  partDead: "yes" | "no" | null;
  partDeadWhat: string;
  /** 5. The second qualified person, from the site's Active QEWs. */
  secondQewId: string;
  /** 5, no other Active QEW at the site: the standby person (rev 3 decision 5). */
  standbyName: string;
  standbyTitle: string;
  /** The three lines the standby ticks on the QEW's phone. */
  standbyTicks: StandbyTick[];
  /** 6. Who calls for help, the nearest upstream disconnect, the way out. */
  emergencyPlan: string;
  /** 7. Can this work be done safely? No ends the request. */
  safeToProceed: "yes" | "no" | null;
}

export function newEewpAnswers(): EewpAnswers {
  return {
    liveWork: "",
    justificationCategories: [],
    justification: "",
    ifDeEnergized: "",
    partDead: null,
    partDeadWhat: "",
    secondQewId: "",
    standbyName: "",
    standbyTitle: "",
    standbyTicks: [],
    emergencyPlan: "",
    safeToProceed: null,
  };
}

const clean = (v: unknown, n = 1000): string => (typeof v === "string" ? v.trim().slice(0, n) : "");

/** The answers from the page, cleaned. Anything unknown is dropped. */
export function eewpAnswersFrom(raw: unknown): EewpAnswers {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const keys = JUSTIFY_OPTIONS.map(([k]) => k) as string[];
  const cats = Array.isArray(r.justificationCategories) ? r.justificationCategories.filter((x): x is JustifyKey => typeof x === "string" && keys.includes(x)) : [];
  const yn = (v: unknown) => (v === "yes" ? "yes" : v === "no" ? "no" : null);
  return {
    liveWork: clean(r.liveWork),
    justificationCategories: [...new Set(cats)],
    justification: clean(r.justification, 2000),
    ifDeEnergized: clean(r.ifDeEnergized),
    partDead: yn(r.partDead),
    partDeadWhat: yn(r.partDead) === "yes" ? clean(r.partDeadWhat) : "",
    secondQewId: clean(r.secondQewId, 40),
    standbyName: clean(r.standbyName, 80),
    standbyTitle: clean(r.standbyTitle, 80),
    standbyTicks: Array.isArray(r.standbyTicks) ? [...new Set(r.standbyTicks.filter((x): x is StandbyTick => typeof x === "string" && STANDBY_KEYS.includes(x)))] : [],
    emergencyPlan: clean(r.emergencyPlan),
    safeToProceed: yn(r.safeToProceed),
  };
}

/** The standby person's three lines, ticked in person on the QEW's phone. */
export const STANDBY_TICKS = [
  ["callHelp", "I know how to call for help."],
  ["disconnect", "I know where the upstream disconnect is."],
  ["outside", "I stay outside the boundary."],
] as const;
export type StandbyTick = (typeof STANDBY_TICKS)[number][0];
const STANDBY_KEYS: string[] = STANDBY_TICKS.map(([k]) => k);

export const SAFE_NO = "Then it is not done live. Go de-energized, or ask for a planned outage.";

/** Null when the seven questions are answered. The second QEW must be one of the site's Active QEWs. */
export function eewpAnswersProblem(e: EewpAnswers, siteQewIds: readonly string[]): string | null {
  if (!e.liveWork) return "1. Say what has to be done live.";
  if (!e.justificationCategories.length) return "2. Pick why it can’t be de-energized.";
  if (!e.justification) return "2. Give the specifics in your words.";
  if (!e.ifDeEnergized) return "3. Say what happens if it is de-energized.";
  if (!e.partDead) return "4. Answer whether part of the job can be done dead.";
  if (e.partDead === "yes" && !e.partDeadWhat) return "4. Say which part can be done dead.";
  if (!siteQewIds.length) {
    // No other Active QEW at the site: a standby person, never nobody (rev 3 decision 5).
    if (!e.standbyName) return "5. Name the standby person.";
    if (!e.standbyTitle) return "5. Give the standby person's job title.";
    if (STANDBY_TICKS.some(([k]) => !e.standbyTicks.includes(k))) return "5. The standby person ticks all three lines.";
  } else {
    if (!e.secondQewId) return "5. Pick the second qualified person.";
    if (!siteQewIds.includes(e.secondQewId)) return "5. The second qualified person must be an Active QEW at this site.";
  }
  if (!e.emergencyPlan) return "6. Say what happens if something goes wrong.";
  if (e.safeToProceed !== "yes") return e.safeToProceed === "no" ? SAFE_NO : "7. Answer whether this work can be done safely.";
  return null;
}

// ---- The JSP's half, carried over and frozen ----

export interface HalfRow {
  section: "Job" | "Shock" | "Arc flash" | "Area and brief";
  label: string;
  value: string;
}

/** NFPA 70E Table 130.4(E)(a), restricted approach, exposed fixed circuit part. */
export function restrictedApproachOf(volts: number | null): string {
  if (volts === null) return "Unknown. Voltage not on record";
  if (volts < 50) return "Not applicable under 50 V";
  if (volts <= 150) return "Avoid contact";
  if (volts <= 750) return "1 ft 0 in";
  if (volts <= 15000) return "2 ft 2 in";
  if (volts <= 36000) return "2 ft 7 in";
  return "See NFPA 70E Table 130.4(E)(a)";
}

export interface HalfInput {
  a: JspAsset;
  t: JspTask;
  s: JspState;
  jspNumber: string;
  location: string;
  requesterName: string;
  requesterId: string;
  scope: EewpScope;
  /** Shock and arc levels from the verdict. */
  shockLevel: string;
  arcLevel: string;
  /** Today, MM/DD, for a glove inspection done on this JSP. */
  todayShort: string;
}

/** The rows the approvers see, as the QEW saw them. Same on the page and on the server. */
export function jspHalfOf(i: HalfInput): HalfRow[] {
  const { a, t, s } = i;
  const ie = a.incidentEnergy === "pending" ? "Pending study" : a.incidentEnergy === "outsideStudy" ? "Outside the arc flash study" : `${a.incidentEnergy} cal/cm²`;
  const gloves = s.gloves === "pass" ? `Pass ${i.todayShort}` : s.gloves === "fail" ? `Failed ${i.todayShort}. Another tested pair needed` : "Inspect before use";
  const rows: HalfRow[] = [
    { section: "Job", label: "Site", value: a.siteName },
    { section: "Job", label: "Equipment", value: [a.equipmentType, i.location || a.area].filter(Boolean).join(" · ") },
    { section: "Job", label: "Asset", value: a.assetId },
    { section: "Job", label: "Task", value: i.scope === "coverRemoval" ? `${t.name} · cover removal and refit only` : t.name },
    { section: "Job", label: "JSP", value: `#${i.jspNumber.replace(/^JSP-/, "")}` },
    { section: "Job", label: "Requester", value: `${i.requesterName} · ${i.requesterId}` },
    { section: "Job", label: "SOP", value: t.sopNumber ?? "None on record" },
    { section: "Shock", label: "Voltage", value: a.volts === null ? "Not on record. Treat as live" : `${a.volts} V` },
    { section: "Shock", label: "Limited approach", value: a.limitedApproach || "Unknown" },
    { section: "Shock", label: "Restricted approach", value: restrictedApproachOf(a.volts) },
    { section: "Shock", label: "Gloves", value: `Class 0 with leather protectors · ${gloves}` },
    { section: "Arc flash", label: "Incident energy", value: ie },
    { section: "Arc flash", label: "Arc flash boundary", value: a.arcFlashBoundary || "Unknown" },
    { section: "Arc flash", label: "Arc PPE", value: `Group 1 (${a.ieCeilingCal} cal) arc-rated PPE` },
    { section: "Area and brief", label: "Boundary", value: s.c.boundary ? "Marked. Unqualified persons excluded" : "Set and marked before work starts" },
    { section: "Area and brief", label: "Job brief", value: s.brief.trim() || "None" },
    { section: "Area and brief", label: "Risk", value: `Shock ${i.shockLevel} · Arc ${i.arcLevel}` },
  ];
  return rows;
}

// ---- The permit, as the JSP page sees it ----

export type EewpStatus = "Waiting" | "Approved" | "Denied" | "Expired" | "Withdrawn" | "Closed";
export const EEWP_STATUSES: EewpStatus[] = ["Waiting", "Approved", "Denied", "Expired", "Withdrawn", "Closed"];

export interface ApproverLine {
  name: string;
  role: string;
  /** Opened the link (rev 3 decision 3). */
  openedAt: string | null;
  decision: "approved" | "denied" | null;
  /** Deny asks for one line. */
  reason: string;
  at: string | null;
}

/** The second QEW, or a standby person when the site has no other Active QEW. */
export interface SecondPerson {
  kind: "qew" | "standby";
  badgeId: string;
  name: string;
  /** Standby only: the job title the QEW typed. */
  title: string;
  /** Agreed. A standby agrees in person when the QEW submits. */
  confirmedAt: string | null;
  /** Said No: the permit ends. */
  declinedAt: string | null;
}

export interface EewpSummary {
  permitId: string;
  jspNumber: string;
  status: EewpStatus;
  scope: EewpScope;
  askedAt: string;
  /** Where the link was tapped: needPower, coverRemoval, lockOut, over40. */
  from: EewpFrom | null;
  approvalsRequired: number;
  approvals: ApproverLine[];
  secondQew: SecondPerson;
  expiresAt: string;
  /** Denied or Expired: the line the QEW sees. */
  endReason: string;
  /** Confirm PPE And Begin: the items to tick. */
  ppeItems: string[];
  ppeConfirmedAt: string | null;
  repeatCount: number;
}

const s_ = (v: unknown): string => (typeof v === "string" ? v : "");
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});

export function secondOf(raw: unknown): SecondPerson {
  const sq = obj(raw);
  return {
    kind: sq.kind === "standby" ? "standby" : "qew",
    badgeId: s_(sq.badgeId),
    name: s_(sq.name),
    title: s_(sq.title),
    confirmedAt: s_(sq.confirmedAt) || null,
    declinedAt: s_(sq.declinedAt) || null,
  };
}

/** The page's view of a permit record. Emails and link codes stay on the server. */
export function summaryOf(d: Record<string, unknown>): EewpSummary {
  const st = s_(d.status) as EewpStatus;
  const approvals = (Array.isArray(d.approvals) ? d.approvals : []).map(obj).map((x) => ({
    name: s_(x.name),
    role: s_(x.role),
    openedAt: s_(x.openedAt) || null,
    decision: x.decision === "approved" ? ("approved" as const) : x.decision === "denied" ? ("denied" as const) : null,
    reason: s_(x.reason),
    at: s_(x.at) || null,
  }));
  const ppe = obj(d.ppeConfirmed);
  return {
    permitId: s_(d.permitId),
    jspNumber: s_(d.jspNumber),
    status: EEWP_STATUSES.includes(st) ? st : "Waiting",
    scope: d.scope === "coverRemoval" ? "coverRemoval" : "full",
    askedAt: s_(d.askedAt),
    from: typeof d.from === "string" && Object.prototype.hasOwnProperty.call(LINK_NODE, d.from) ? (d.from as EewpFrom) : null,
    approvalsRequired: typeof d.approvalsRequired === "number" ? d.approvalsRequired : approvals.length,
    approvals,
    secondQew: secondOf(d.secondQew),
    expiresAt: s_(d.expiresAt),
    endReason: s_(d.endReason),
    ppeItems: Array.isArray(d.ppeItems) ? d.ppeItems.filter((x): x is string => typeof x === "string") : [],
    ppeConfirmedAt: s_(ppe.at) || null,
    repeatCount: typeof d.repeatCount === "number" ? d.repeatCount : 0,
  };
}

export function approvedCount(p: EewpSummary): number {
  return p.approvals.filter((x) => x.decision === "approved").length;
}

/** "No work starts until both say yes." */
export function untilLine(required: number): string {
  return required === 1 ? "No work starts until the approver says yes." : required === 2 ? "No work starts until both say yes." : `No work starts until all ${required} say yes.`;
}

/** "Waiting on 2 approvers." on the Before You Ask cost box. */
export function waitingOn(required: number): string {
  return `Waiting on ${required} ${required === 1 ? "approver" : "approvers"}.`;
}

// ---- The outage request (Before You Ask, screen 2) ----

/** Yes, Request An Outage: one work request line. The JSP stops. */
export function outageLine(taskName: string): { finding: string; priority: "Medium" } {
  return { finding: `Planned outage needed: ${taskName}`, priority: "Medium" };
}

/** "5:42 PM" in site time. */
export function clockOf(iso: string, timeZone = "America/New_York"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" }).format(d);
}

/** The node each link sits on. */
export const LINK_NODE: Record<EewpFrom, string> = { needPower: "Do The Work", coverRemoval: "PPE And Boundary", lockOut: "Lock Out", over40: "Risk" };

// ---- PR 3: the approver side (EEWP_WORDS.md rev 3) ----
// Every ticked approver must say yes, and the second QEW or standby must have agreed. One Deny ends it.
// A second QEW No ends it. Silence never approves: 12 hours and it expires.

/** What the permit is now. Waiting until every yes is in, or something ends it. */
export function outcomeOf(
  p: { status: string; approvals: { decision: string | null; name: string; reason?: string }[]; secondQew: SecondPerson; expiresAt: string },
  nowIso: string,
): { status: EewpStatus; endReason: string } {
  if (p.status !== "Waiting") return { status: (EEWP_STATUSES as string[]).includes(p.status) ? (p.status as EewpStatus) : "Waiting", endReason: "" };
  const denied = p.approvals.find((x) => x.decision === "denied");
  if (denied) return { status: "Denied", endReason: `Denied by ${denied.name}: ${denied.reason ?? ""}`.trim() };
  if (p.secondQew.declinedAt) return { status: "Denied", endReason: `${p.secondQew.name} did not agree it can be done safely.` };
  if (p.approvals.length > 0 && p.approvals.every((x) => x.decision === "approved") && p.secondQew.confirmedAt) return { status: "Approved", endReason: "" };
  if (p.expiresAt && nowIso >= p.expiresAt) return { status: "Expired", endReason: "No decision in 12 hours." };
  return { status: "Waiting", endReason: "" };
}

/** Deny asks for one line. */
export function decideProblem(decision: unknown, reason: unknown): string | null {
  if (decision !== "approved" && decision !== "denied") return "Approve or Deny?";
  if (decision === "denied" && !(typeof reason === "string" && reason.trim())) return "Give one line for the Deny. The QEW sees it.";
  return null;
}

/** "2nd live-work permit on this equipment in 12 months" */
export function repeatLine(repeatCount: number): string {
  if (repeatCount < 1) return "";
  const n = repeatCount + 1;
  const nth = n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
  return `${nth} live-work permit on this equipment in 12 months`;
}

/** The danger, in plain consequences for a non-technical reader. */
export function howDangerous(volts: number | null, ie: number | "pending" | "outsideStudy" | null): string {
  const shock = volts === null ? "Voltage not on record. Treat it as live, it can kill on contact." : `${volts} volts, can kill on contact.`;
  const arc =
    typeof ie === "number" ? `Arc flash energy ${ie} cal/cm², needs a full arc flash suit.` : "Arc flash energy not studied yet. Full arc flash suit.";
  return `${shock} ${arc}`;
}

/** Who stands with the QEW, as the approvers read it. */
export function protectionLine(sq: SecondPerson): string {
  const rest = "arc flash suit, insulated gloves, barricaded area";
  return sq.kind === "standby" ? `No second QEW at this site. Standby: ${sq.name}, ${sq.title}. Plus ${rest}.` : `Second qualified worker ${sq.name}, ${rest}.`;
}

/** Confirm PPE And Begin: each item the QEW ticks before the JSP opens (itemized, as live S101). */
export function ppeItemsOf(arcPpe: string, sq: SecondPerson): string[] {
  return [
    arcPpe ? `Arc flash suit and hood: ${arcPpe}` : "Arc flash suit and hood",
    "Class 0 gloves with leather protectors, inspected",
    "Safety glasses and hearing protection",
    `${sq.name} in position outside the arc flash boundary`,
  ];
}

/** The fields anyone shown the permit may see. No emails, no link codes. */
export interface PermitView {
  permitId: string;
  jspNumber: string;
  status: EewpStatus;
  scope: EewpScope;
  siteName: string;
  assetId: string;
  equipment: string;
  location: string;
  taskName: string;
  requester: { badgeId: string; name: string };
  secondQew: SecondPerson;
  liveWork: string;
  justificationCategories: string[];
  justification: string;
  ifDeEnergized: string;
  partDead: string;
  partDeadWhat: string;
  emergencyPlan: string;
  jspHalf: HalfRow[];
  dangerLine: string;
  protection: string;
  history: string;
  approvals: ApproverLine[];
  approvalsRequired: number;
  expiresAt: string;
  createdAt: string;
  endReason: string;
  ppeItems: string[];
  ppeConfirmedAt: string | null;
}

export function permitViewOf(d: Record<string, unknown>): PermitView {
  const sum = summaryOf(d);
  const req = obj(d.requester);
  const half = (Array.isArray(d.jspHalf) ? d.jspHalf : []).map(obj).map((r) => ({ section: s_(r.section) as HalfRow["section"], label: s_(r.label), value: s_(r.value) }));
  const volts = typeof d.volts === "number" ? d.volts : null;
  const ie = typeof d.incidentEnergy === "number" ? d.incidentEnergy : d.incidentEnergy === "outsideStudy" ? "outsideStudy" : "pending";
  return {
    permitId: sum.permitId,
    jspNumber: sum.jspNumber,
    status: sum.status,
    scope: sum.scope,
    siteName: s_(d.siteName),
    assetId: s_(d.assetId),
    equipment: s_(d.equipment),
    location: s_(d.location),
    taskName: s_(d.taskName),
    requester: { badgeId: s_(req.badgeId), name: s_(req.name) },
    secondQew: sum.secondQew,
    liveWork: s_(d.liveWork),
    justificationCategories: Array.isArray(d.justificationCategories) ? d.justificationCategories.filter((x): x is string => typeof x === "string") : [],
    justification: s_(d.justification),
    ifDeEnergized: s_(d.ifDeEnergized),
    partDead: s_(d.partDead),
    partDeadWhat: s_(d.partDeadWhat),
    emergencyPlan: s_(d.emergencyPlan),
    jspHalf: half,
    dangerLine: howDangerous(volts, ie),
    protection: protectionLine(sum.secondQew),
    history: repeatLine(sum.repeatCount),
    approvals: sum.approvals,
    approvalsRequired: sum.approvalsRequired,
    expiresAt: sum.expiresAt,
    createdAt: s_(d.createdAt),
    endReason: sum.endReason,
    ppeItems: sum.ppeItems,
    ppeConfirmedAt: sum.ppeConfirmedAt,
  };
}

export const COVER_SCOPE_LINE = "This permit covers removing and refitting the covers only. The scan itself is exempt.";
export const UNSURE_LINE = "If you are unsure, deny it. The work can still be done with the power off.";
export const DANGER_LINE =
  "Normally we shut the power off and lock it before anyone works on it. This permit asks to skip that step. Working with the power on raises the risk of shock and arc flash for anyone on the job. If something goes wrong, it can cause severe burns or death.";

const justifyWord = (k: string) => JUSTIFY_OPTIONS.find(([key]) => key === k)?.[1] ?? k;

/** The approver email rows (EEWP_WORDS.md rev 3, "The approver email"). */
export function approverRows(v: PermitView): { label: string; value: string }[] {
  return [
    { label: "The work", value: v.liveWork },
    { label: "Why the power can't be shut off", value: [v.justificationCategories.map(justifyWord).join(", "), v.justification].filter(Boolean).join(". ") },
    { label: "What happens if we shut it off", value: v.ifDeEnergized },
    { label: "How dangerous", value: v.dangerLine },
    { label: "Protection in place", value: v.protection },
    ...(v.scope === "coverRemoval" ? [{ label: "Scope", value: COVER_SCOPE_LINE }] : []),
    ...(v.history ? [{ label: "Equipment history", value: v.history }] : []),
    { label: "Expires", value: `12 hours after the request, ${clockOf(v.expiresAt)}` },
    { label: "Permit", value: `${v.permitId} · JSP #${v.jspNumber.replace(/^JSP-/, "")}` },
  ];
}

/** The decision link the approver's email carries. One per approver, single use. */
export function decisionPath(permitId: string, code: string, companyId: string): string {
  return `?eewp=${encodeURIComponent(permitId)}&k=${encodeURIComponent(code)}&co=${encodeURIComponent(companyId)}`;
}

export function decisionParamsFrom(search: string): { permitId: string; code: string; company: string } | null {
  const p = new URLSearchParams(search);
  const permitId = (p.get("eewp") ?? "").trim();
  if (!/^EEWP-\d{4,}$/.test(permitId)) return null;
  return { permitId, code: (p.get("k") ?? "").trim(), company: (p.get("co") ?? "alltech").trim() || "alltech" };
}

// ---- The emails (plain text fields; the sender builds the page) ----

export interface EewpMail {
  to: { name: string; email: string }[];
  toLabel: string;
  subject: string;
  headline: string;
  lines: string[];
  rows: { label: string; value: string }[];
  button: { label: string; path: string } | null;
  footer: string;
  reason: string;
}

const qName = (v: PermitView) => v.requester.name || v.requester.badgeId;
const eq = (v: PermitView) => v.equipment || v.assetId;

/** One per approver, each with their own link. */
export function approverMail(v: PermitView, to: { name: string; email: string; role: string }, path: string): EewpMail {
  return {
    to: [{ name: to.name, email: to.email }],
    toLabel: `${to.name}, ${to.role}`,
    subject: `Approval needed: live electrical work · ${eq(v)} · ${v.siteName}`,
    headline: `${qName(v)} is asking to work on electrical equipment while the power is on.`,
    lines: [DANGER_LINE],
    rows: approverRows(v),
    button: { label: "Review And Decide", path },
    footer: `${UNSURE_LINE} You get this because you are an EEWP Approver at ${v.siteName}. This link is yours alone and works once.`,
    reason: "EEWP approval",
  };
}

/** Awareness (site, no limit) and the ESP Manager (program EEWP Observer). No link, no decision. */
export function awarenessMail(v: PermitView, to: { name: string; email: string }[], why: string): EewpMail {
  const approvers = v.approvals.map((a) => a.name).join(", ");
  const second = v.secondQew.kind === "standby" ? `Standby ${v.secondQew.name}, ${v.secondQew.title}` : `Second QEW ${v.secondQew.name}`;
  return {
    to,
    toLabel: why,
    subject: `EEWP requested · ${eq(v)} · ${v.siteName}`,
    headline: `${qName(v)} asked to work on ${eq(v)} with the power on.`,
    lines: [],
    rows: [
      { label: "The work", value: v.liveWork },
      { label: "Why", value: v.justification },
      { label: "Approvers", value: approvers || "None" },
      { label: "With", value: second },
      ...(v.history ? [{ label: "Equipment history", value: v.history }] : []),
      { label: "Expires", value: clockOf(v.expiresAt) },
      { label: "Permit", value: `${v.permitId} · JSP #${v.jspNumber.replace(/^JSP-/, "")}` },
    ],
    button: null,
    footer: `For your awareness. No action needed. You get this as ${why}.`,
    reason: "EEWP awareness",
  };
}

/** The named second QEW: review and agree on the Dashboard, with the bag tag. */
export function secondQewMail(v: PermitView, to: { name: string; email: string }): EewpMail {
  return {
    to: [to],
    toLabel: `${to.name}, second QEW`,
    subject: `You are second QEW on ${v.permitId} · ${eq(v)} · ${v.siteName}`,
    headline: `${qName(v)} named you as second QEW on ${v.permitId}.`,
    lines: ["Scan your bag tag and open your Dashboard. Review the plan and tap I Agree, or No. This is agreement as a QEW, not an approval."],
    rows: [
      { label: "The work", value: v.liveWork },
      { label: "Equipment", value: `${eq(v)} · ${v.assetId}` },
      { label: "If something goes wrong", value: v.emergencyPlan },
      { label: "Expires", value: clockOf(v.expiresAt) },
    ],
    button: null,
    footer: `You get this because ${qName(v)} named you on this permit.`,
    reason: "EEWP second QEW",
  };
}

/** One email to the QEW when it ends (rev 3 decision 7). */
export function outcomeMail(v: PermitView, to: { name: string; email: string }): EewpMail {
  const num = `JSP #${v.jspNumber.replace(/^JSP-/, "")}`;
  const approvedBy = v.approvals.filter((a) => a.decision === "approved").map((a) => a.name).join(" and ");
  const head =
    v.status === "Approved"
      ? `${v.permitId} is approved.`
      : v.status === "Denied"
        ? `${v.permitId} is denied.`
        : `${v.permitId} expired.`;
  const lines =
    v.status === "Approved"
      ? [`Approved by ${approvedBy}. Open ${num}, confirm your PPE, and begin. Valid for this JSP only.`]
      : [`${v.endReason} Go de-energized, or discard ${num}.`];
  return {
    to: [to],
    toLabel: to.name,
    subject: `${v.permitId} ${v.status.toLowerCase()} · ${eq(v)} · ${v.siteName}`,
    headline: head,
    lines,
    rows: [
      { label: "The work", value: v.liveWork },
      { label: "Equipment", value: `${eq(v)} · ${v.assetId}` },
      { label: "JSP", value: num },
    ],
    button: null,
    footer: "You get this because you asked for this permit. One email when it is decided.",
    reason: "EEWP decided",
  };
}

// ---- Print: laid out like form 28.5 for the site safety file ----

function e_(v: unknown): string {
  return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function printHtmlOf(v: PermitView): string {
  const row = (k: string, val: string) => `<tr><th>${e_(k)}</th><td>${e_(val || "Not given")}</td></tr>`;
  const sec = (t: string) => `<tr class="h"><td colspan="2">${e_(t)}</td></tr>`;
  const halfBy = (section: string) => v.jspHalf.filter((r) => r.section === section).map((r) => row(r.label, r.value)).join("");
  const appr = v.approvals
    .map((a) => row(`${a.name}, ${a.role}`, a.decision ? `${a.decision === "approved" ? "Approved" : `Denied: ${a.reason}`} · ${a.at ? new Date(a.at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}` : "No decision"))
    .join("");
  const sq = v.secondQew;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${e_(v.permitId)} · Energized Electrical Work Permit</title>
<style>body{font:12px/1.4 Arial,sans-serif;color:#111;margin:24px}h1{font-size:18px;margin:0}p.s{margin:2px 0 14px;color:#444}
table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{border:1px solid #999;padding:5px 7px;text-align:left;vertical-align:top}
th{width:34%;background:#f3f3f3;font-weight:700}tr.h td{background:#111;color:#F5C400;font-weight:800;letter-spacing:.5px;text-transform:uppercase;font-size:11px}
.sig{display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:20px}.sig div{border-top:1px solid #111;padding-top:4px;font-size:11px}
@media print{body{margin:10mm}}</style></head><body>
<h1>Energized Electrical Work Permit · ${e_(v.permitId)}</h1><p class="s">Form 28.5 layout · SOP 28.5 · ${e_(v.siteName)} · Status: ${e_(v.status)}${v.endReason ? ` · ${e_(v.endReason)}` : ""}</p>
<table>${sec("Part I · Work requested")}${row("Requested by", `${v.requester.name} · ${v.requester.badgeId}`)}${row("Equipment", `${v.equipment} · ${v.location}`)}${row("Asset", v.assetId)}${row("Task", v.taskName)}${row("JSP", `#${v.jspNumber.replace(/^JSP-/, "")}`)}
${v.scope === "coverRemoval" ? row("Scope", COVER_SCOPE_LINE) : ""}${row("Work to be done live", v.liveWork)}${row("Why it cannot be de-energized", [v.justificationCategories.map(justifyWord).join(", "), v.justification].filter(Boolean).join(". "))}${row("If de-energized", v.ifDeEnergized)}${row("Part done dead", v.partDead === "yes" ? `Yes. ${v.partDeadWhat}` : "No")}</table>
<table>${sec("Part II · Safe work practices")}${halfBy("Shock")}${halfBy("Arc flash")}${halfBy("Area and brief")}${row("SOP", v.jspHalf.find((r) => r.label === "SOP")?.value ?? "")}${row("If something goes wrong", v.emergencyPlan)}
${row(sq.kind === "standby" ? "Standby person" : "Second qualified person", `${sq.name}${sq.kind === "standby" ? `, ${sq.title}` : ` · ${sq.badgeId}`} · ${sq.confirmedAt ? "Agreed" : sq.declinedAt ? "Did not agree" : "Not yet"}`)}
${row("PPE confirmed", v.ppeConfirmedAt ? `${v.ppeItems.join("; ")}` : "Not yet")}${v.history ? row("Equipment history", v.history) : ""}</table>
<table>${sec(`Part III · Approval · every approver must say yes (${v.approvalsRequired})`)}${appr}</table>
<div class="sig"><div>Requesting QEW signature</div><div>Date</div></div>
<script>window.onload=function(){window.print()}</script></body></html>`;
}

/** The amber Review And Agree card on the second QEW's Dashboard (rev 3 decision 4). */
export interface EewpAsk {
  permitId: string;
  requesterName: string;
  assetId: string;
  equipment: string;
  liveWork: string;
  emergencyPlan: string;
  expiresAt: string;
  answered: boolean;
}

export function askOf(d: Record<string, unknown>): EewpAsk {
  const sq = secondOf(d.secondQew);
  return {
    permitId: s_(d.permitId),
    requesterName: s_(obj(d.requester).name),
    assetId: s_(d.assetId),
    equipment: s_(d.equipment),
    liveWork: s_(d.liveWork),
    emergencyPlan: s_(d.emergencyPlan),
    expiresAt: s_(d.expiresAt),
    answered: Boolean(sq.confirmedAt || sq.declinedAt),
  };
}
